

import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import "../styles/boss.css";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
  LineChart, Line,
  AreaChart, Area,
  CartesianGrid
} from "recharts";

function BossDashboard() {

  const [employees, setEmployees] = useState(0);
  const [meetings, setMeetings] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);

  const [pending, setPending] = useState(0);
  const [approved, setApproved] = useState(0);

  // ✅ NEW STATES (IMPORTANT)
  const [totalCalls, setTotalCalls] = useState(0);
  const [totalDeals, setTotalDeals] = useState(0);
  const [totalMeetings, setTotalMeetings] = useState(0);
  const [totalFollowups, setTotalFollowups] = useState(0);
  const [totalConversions, setTotalConversions] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {

      const role = localStorage.getItem("role");
      const userId = localStorage.getItem("userId");

      // ✅ DASHBOARD DATA (TOTAL COUNTS)
      const res = await axios.get(
        `http://localhost:5000/api/dashboard?role=${role}&user_id=${userId}`
      );

      setTotalCalls(res.data.totalCalls);
      setTotalDeals(res.data.totalDeals);
      setTotalMeetings(res.data.totalMeetings);
      setTotalFollowups(res.data.followups);
      setTotalConversions(res.data.conversions);
      setEmployees(res.data.totalEmployees);

      // ✅ MEETINGS LIST (for table)
      const meetingRes = await axios.get(
        `http://localhost:5000/api/meetings?role=${role}&user_id=${userId}`
      );

      setMeetings(meetingRes.data);

      // ✅ SALES USERS
      const usersRes = await axios.get(
        "http://localhost:5000/api/users/sales-users"
      );

      setSalesUsers(usersRes.data);

      // ✅ STATUS COUNT
      const pendingCount = meetingRes.data.filter(m => m.status === "pending").length;
      const approvedCount = meetingRes.data.filter(m => m.status === "approved").length;

      setPending(pendingCount);
      setApproved(approvedCount);

    } catch (err) {
      console.log(err);
    }
  };

  const handleAssign = async (meetingId, userId) => {
    await axios.put(
      `http://localhost:5000/api/meetings/approve/${meetingId}`,
      { assigned_to: userId }
    );
    fetchData();
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN") : "-";

  const formatTime = (t) => {
    if (!t) return "-";
    const [h, m] = t.split(":");
    const date = new Date();
    date.setHours(h, m);
    return date.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  // 📊 CHART DATA
  const chartData = meetings.map(m => ({
    name: m.company_name || "No Name",
    meetings: 1
  }));

  const pieData = [
    { name: "Approved", value: approved },
    { name: "Pending", value: pending }
  ];

  const monthly = ["Jan","Feb","Mar","Apr","May","Jun","Jul"];
  const trendData = monthly.map((m, i) => ({
    month: m,
    meetings: meetings.filter((_, index) => index % 6 === i).length
  }));

  const COLORS = ["#22c55e", "#f59e0b"];

  return (
    <div className="dashboard-page">

      {/* HEADER */}
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="page-title"
      >
        🚀 Boss Dashboard
      </motion.h2>

      {/* 🔥 KPI CARDS (TOTAL DATA) */}
      <div className="kpi-grid">

        {[
          { title: "Employees", value: employees },
          { title: "Total Calls", value: totalCalls },
          { title: "Conversions", value: totalConversions },
          { title: "Followups", value: totalFollowups },
          { title: "Meetings", value: totalMeetings },
          { title: "Deals", value: totalDeals },
          { title: "Approved", value: approved },
          { title: "Pending", value: pending },
        ].map((item, i) => (

          <motion.div
            key={i}
            className="kpi-card"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            whileHover={{ scale: 1.05 }}
          >
            <p>{item.title}</p>
            <h2>{item.value}</h2>
          </motion.div>

        ))}

      </div>

      {/* CHARTS */}
      <div className="chart-grid">

        <div className="chart-box">
          <h4>Meetings by Company</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" hide />
              <YAxis />
              <Tooltip />
              <Bar dataKey="meetings" fill="#4f46e5" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-box">
          <h4>Status Overview</h4>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={pieData} dataKey="value" innerRadius={60}>
                {pieData.map((e, i) => (
                  <Cell key={i} fill={COLORS[i]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-box">
          <h4>Meeting Trend</h4>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={trendData}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="meetings" stroke="#4f46e5" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-box">
          <h4>Growth Analysis</h4>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={trendData}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="meetings" fill="#4f46e5" stroke="#4f46e5" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* TABLE */}
      <div className="table-box">

        <h3>Meeting Assignments</h3>

        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Client</th>
              <th>Date</th>
              <th>Time</th>
              <th>Assign</th>
            </tr>
          </thead>

          <tbody>
            {meetings.map(m => (
              <tr key={m.id}>
                <td>{m.company_name}</td>
                <td>{m.client_name}</td>
                <td>{formatDate(m.meeting_date)}</td>
                <td>{formatTime(m.meeting_time)}</td>

                <td>
                  <select onChange={(e) => handleAssign(m.id, e.target.value)}>
                    <option value="">Assign</option>
                    {salesUsers.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>

        </table>

      </div>

    </div>
  );
}

export default BossDashboard;